'use client'

import { css } from '@lastbrand/web-ui-styled-system/css'
import classnames from 'classnames'
import { Fragment, useCallback, useMemo, useState } from 'react'

import { PLRenderedChildWidget } from '@src/components/pl-base-widget'
import { TEST_LOCATORS } from '@src/constants/test-locators'
import reviewHighlightStyles from '@src/quince-components/src/atoms/reviewHighLightsSection/reviewHighlightsSectionV2.module.scss'
import useDeviceInfo, { DEVICE_TYPES } from '@src/quince-site-common/src/hooks/useDeviceInfo'
// PL-only photo-popup state manager + popups (copies of the legacy ones, with pagination). Used so
// the shared legacy popups/manager stay untouched.
// Reuse the legacy mobile "Review Highlights" (newPdpv2) styles so the PL mini surface matches non-PL.
// Reuse the legacy full-reviews section styles (summary padding/margins, gallery margins) so the PL
// FULL surface visually matches non-PL ProductReviews — same classes the organism applies.
import PLReviewPopupDesktop from '@src/quince-site-pdp/src/atoms/reviewPopUp/PLReviewPopupDesktop'
import PLReviewPopupMobile from '@src/quince-site-pdp/src/atoms/reviewPopUp/reviewPopupMobileNative/PLReviewPopupMobile'
import reviewSectionStyles from '@src/quince-site-pdp/src/organisms/productPageSections/productReviews/productReviews.module.scss'
import { shouldUseDesignSystem } from '@src/utils/should-use-design-system'

import { PLReviewPopupDS } from './pl-review-popup-ds'
import { ReviewsProductContext, ReviewsVariant, ReviewsVariantContext } from './reviews.context'

// DS full-variant section wrapper (matches product-reviews-ds.tsx's mainContainerCss).
const mainContainerCss = css.raw({
  width: '100%',
  maxWidth: '1600px',
  marginInline: 'auto',
  paddingX: { base: '0', lg: 'space-48' },
})

// DS mini-variant container (matches review-highlights-ds.tsx's containerCss).
const miniContainerCss = css({
  display: { base: 'flex', lg: 'none' },
  flexDirection: 'column',
  gap: 'space-16',
  marginX: 'space-16',
  paddingTop: 'space-40',
  paddingBottom: 'space-24',
  borderTop: '1px solid',
  borderBottom: '1px solid',
  // Figma divider color `custom/black-trans-20` (black @20%); no alpha token in the DS
  // scale, so use the literal rgba — matches the sibling PDP DS dividers.
  borderColor: 'rgba(33, 32, 31, 0.2)',
})

// ---------------- Shared container (REVIEWS_MINI / REVIEWS_FULL) ----------------
// Arranges the child widgets in order; tags the subtree with its surface variant.
export function ReviewsContainer({
  variant,
  renderedChildren,
}: {
  variant: ReviewsVariant
  renderedChildren?: readonly PLRenderedChildWidget[]
}) {
  const { deviceType } = useDeviceInfo()
  const isMobile = deviceType === DEVICE_TYPES.MOBILE
  const isDesignSystem = shouldUseDesignSystem()
  // Shared spid holder (see ReviewsProductContext) — set once by the first review child that has it.
  const [spid, setSpid] = useState<string | null>(null)
  const publishSpid = useCallback((next: string) => {
    setSpid(prev => prev ?? next)
  }, [])
  const productContextValue = useMemo(() => ({ spid, publishSpid }), [spid, publishSpid])
  const children = renderedChildren ?? []
  if (children.length === 0) return null
  const inner = children.map(child => <Fragment key={child.id || child.type}>{child.node}</Fragment>)
  return (
    <ReviewsProductContext.Provider value={productContextValue}>
      <ReviewsVariantContext.Provider value={variant}>
        {variant === 'mini' ? (
          isDesignSystem ? (
            // Same locator as the non-PL review-highlights-ds section — PL and non-PL never
            // co-mount, so automation gets one selector across both modes.
            <div className={miniContainerCss} data-testid={TEST_LOCATORS.PDP_SECTIONS.REVIEW_HIGHLIGHTS}>
              {inner}
            </div>
          ) : (
            // Match the legacy mobile "Review Highlights" container chrome (padding/border).
            <div className={reviewHighlightStyles.container}>
              <div className={reviewHighlightStyles.container__wrapper}>{inner}</div>
            </div>
          )
        ) : isDesignSystem ? (
          // Same locator as the non-PL product-reviews-ds section wrapper (see above).
          <div className={css(mainContainerCss)} data-testid={TEST_LOCATORS.PDP_SECTIONS.REVIEWS_SECTION}>
            {inner}
          </div>
        ) : (
          // Match the legacy non-PL section wrapper (.productReviews): desktop horizontal padding
          // (0 7.6rem) + centered 128.8rem content width, and the newPdpv2 mobile spacing/no-border.
          // Without this the FULL reviews ran flush to the content-column edge on desktop.
          <div className={classnames(reviewSectionStyles.productReviews, reviewSectionStyles.productReviews_newPdpv2)}>
            {inner}
          </div>
        )}
        {/* PL-only photo popup, mounted in the FULL reviews subtree (not the global app layout) so its
          pagination stays PL-scoped. Opened by the PL gallery/list via plReviewPhotoPopupStateManager;
          device picks the mobile vs desktop variant on legacy; the DS popup is responsive. */}
        {variant === 'full' &&
          (isDesignSystem ? <PLReviewPopupDS /> : isMobile ? <PLReviewPopupMobile /> : <PLReviewPopupDesktop />)}
      </ReviewsVariantContext.Provider>
    </ReviewsProductContext.Provider>
  )
}
